import React, { useMemo } from 'react';
import { ClipboardCheck, Calculator, PackagePlus, Info, Layers, Warehouse } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { TIPOS_CONTEO, TIPO_LABELS_CONTEO, EGG_LABELS } from './constants';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const formatLoteLabel = (codigo, loteId) => {
  const code = String(codigo || '').trim();
  if (!code) return `Lote ${(loteId || '').slice(0, 6)}`;
  if (/^lote\b/i.test(code)) return code;
  return `Lote ${code}`;
};

/**
 * Modal para crear/editar un conteo físico (semanal).
 *
 * Muestra dos vistas complementarias:
 *  1) Existencias en Almacén: tarjeta rápida con el stock global por tipo
 *     (útil para chequear a un vistazo mientras se cuenta).
 *  2) Conteo detallado: cada tipo se **subdivide en sus lotes activos** para
 *     que el usuario cuente lote por lote. El "físico" por tipo se calcula
 *     automáticamente como la suma de los físicos de sus lotes; si un tipo no
 *     tiene lotes activos, cae al input directo por tipo (comportamiento
 *     anterior).
 *
 * Nota: El **Conteo Diario** (otra pestaña) sigue como estaba (total diario);
 * este modal es solo para el conteo semanal físico.
 */
export const ConteoModal = ({
  open,
  onOpenChange,
  editing,
  canVerSistemaConteo,
  form,
  setForm,
  savingConteo,
  onSave,
  onFechaChange,
  lotesPorTipo = {},
  stockGlobal = {},
}) => {
  // Actualiza un campo del conteo por tipo (usado por tipos sin lotes)
  const setConteoValue = (tipo, field, value) => {
    setForm((f) => ({
      ...f,
      conteos: {
        ...f.conteos,
        [tipo]: { ...f.conteos[tipo], [field]: value },
      },
    }));
  };

  // Actualiza el "físico" de un lote específico y recalcula el "físico" total
  // del tipo como la suma de todos sus lotes.
  const setLoteFisico = (tipo, loteId, value) => {
    setForm((f) => {
      const filas = [...(f.conteos_por_lote || [])];
      const idx = filas.findIndex((r) => r.tipo_huevo === tipo && r.lote_id === loteId);
      if (idx >= 0) filas[idx] = { ...filas[idx], fisico: value };
      const sumaTipo = filas
        .filter((r) => r.tipo_huevo === tipo)
        .reduce((acc, r) => acc + (parseInt(r.fisico, 10) || 0), 0);
      return {
        ...f,
        conteos_por_lote: filas,
        conteos: {
          ...f.conteos,
          [tipo]: { ...(f.conteos[tipo] || {}), fisico: String(sumaTipo) },
        },
      };
    });
  };

  // Filas por lote agrupadas por tipo — solo lotes con stock (o con dato ya
  // cargado en el conteo) para no ensuciar la vista con lotes agotados.
  const rowsByTipo = useMemo(() => {
    const map = {};
    TIPOS_CONTEO.forEach((t) => { map[t] = []; });
    (form.conteos_por_lote || []).forEach((r) => {
      if (map[r.tipo_huevo]) map[r.tipo_huevo].push(r);
    });
    return map;
  }, [form.conteos_por_lote]);

  // Total de físicos y sistema (contando ambos: por-lote y tipos sueltos)
  const calcTotals = () => {
    let totSis = 0, totFis = 0;
    TIPOS_CONTEO.forEach((t) => {
      const c = form.conteos[t] || {};
      totSis += Number(c.sistema || 0);
      totFis += parseInt(c.fisico, 10) || 0;
    });
    return { totSis, totFis, totDif: totFis - totSis };
  };

  const stockKey = (t) => (t === 'pequeño' ? 'pequeno' : t);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="modal-conteo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            {form.cotejo_inicial ? (
              <PackagePlus className="w-5 h-5 text-green-600" />
            ) : (
              <ClipboardCheck className="w-5 h-5" />
            )}
            {editing
              ? (form.cotejo_inicial ? 'Editar Cotejo de Inventario Inicial' : 'Editar Conteo Físico')
              : (form.cotejo_inicial ? 'Nuevo Cotejo de Inventario Inicial' : 'Nuevo Conteo Físico')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Modo: Cotejo Inicial (SUMA) vs Conteo Físico (AJUSTA) */}
          <div
            className={`rounded-lg border px-3 py-3 flex items-start gap-3 ${
              form.cotejo_inicial
                ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900/50'
                : 'bg-accent border-border'
            }`}
            data-testid="modo-cotejo-wrap"
          >
            <Switch
              checked={!!form.cotejo_inicial}
              onCheckedChange={(v) => setForm((f) => ({ ...f, cotejo_inicial: !!v }))}
              disabled={!!editing}
              data-testid="switch-cotejo-inicial"
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Label
                htmlFor="switch-cotejo-inicial"
                className="text-sm font-semibold cursor-pointer flex items-center gap-2"
              >
                Cotejo de Inventario Inicial
                {form.cotejo_inicial && (
                  <Badge className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">
                    SUMA al stock
                  </Badge>
                )}
              </Label>
              <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                {form.cotejo_inicial
                  ? 'Los valores ingresados se SUMARÁN al inventario actual (uso: cargar inventario inicial junto con lo ya existente).'
                  : 'Los valores ingresados AJUSTARÁN el inventario al físico (uso: conteo regular de verificación). Activa el switch si quieres sumar en vez de ajustar.'}
              </p>
            </div>
          </div>

          {/* Panel: Existencias en Almacén (visible solo si el usuario puede
              ver el sistema; se oculta en Modo Conteo Ciego). */}
          {canVerSistemaConteo && !form.cotejo_inicial && (
            <div
              className="rounded-lg border border-primary/20 bg-primary/5 p-3"
              data-testid="panel-existencias-almacen"
            >
              <div className="flex items-center gap-1.5 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground mb-2">
                <Warehouse className="w-3.5 h-3.5" /> Existencias en el Almacén
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {TIPOS_CONTEO.map((t) => {
                  const val = Number(stockGlobal[stockKey(t)] || stockGlobal[t] || 0);
                  return (
                    <div
                      key={t}
                      className="rounded-md bg-background/70 border border-border/60 px-2 py-1.5"
                    >
                      <div className="text-[10px] text-muted-foreground">{EGG_LABELS[stockKey(t)] || TIPO_LABELS_CONTEO[t]}</div>
                      <div className="text-sm font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                        {fmt(val)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="conteo-fecha">Fecha de Conteo *</Label>
              <Input
                id="conteo-fecha"
                type="date"
                value={form.fecha_conteo}
                onChange={(e) => {
                  const nueva = e.target.value;
                  setForm((f) => ({ ...f, fecha_conteo: nueva }));
                  if (typeof onFechaChange === 'function') onFechaChange(nueva);
                }}
                className="h-10 mt-1.5"
                data-testid="input-fecha-conteo"
              />
            </div>
            {canVerSistemaConteo && !form.cotejo_inicial && (() => {
              const { totSis, totDif } = calcTotals();
              const exact = totSis > 0
                ? Math.max(0, 100 - (Math.abs(totDif) / totSis) * 100)
                : 100;
              const difCls = totDif < 0
                ? 'text-destructive'
                : (totDif > 0 ? 'text-green-600' : 'text-muted-foreground');
              return (
                <>
                  <div className="bg-accent rounded-lg px-3 py-2 flex flex-col justify-center">
                    <p className="text-[11px] text-muted-foreground">Diferencia total</p>
                    <p
                      className={`text-lg font-bold tabular-nums ${difCls}`}
                      style={{ fontFamily: 'Space Grotesk' }}
                    >
                      {totDif > 0
                        ? `+${totDif.toLocaleString('en-US')}`
                        : totDif.toLocaleString('en-US')}
                    </p>
                  </div>
                  <div className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 flex flex-col justify-center">
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Calculator className="w-3 h-3" /> Exactitud
                    </p>
                    <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                      {exact.toFixed(1)}%
                    </p>
                  </div>
                </>
              );
            })()}
            {canVerSistemaConteo && form.cotejo_inicial && (() => {
              const { totFis, totSis } = calcTotals();
              return (
                <>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 rounded-lg px-3 py-2 flex flex-col justify-center">
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <PackagePlus className="w-3 h-3" /> Total a Sumar
                    </p>
                    <p
                      className="text-lg font-bold tabular-nums text-green-700 dark:text-green-400"
                      style={{ fontFamily: 'Space Grotesk' }}
                    >
                      +{totFis.toLocaleString('en-US')}
                    </p>
                  </div>
                  <div className="bg-accent rounded-lg px-3 py-2 flex flex-col justify-center">
                    <p className="text-[11px] text-muted-foreground">Nuevo Total Stock</p>
                    <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                      {(totSis + totFis).toLocaleString('en-US')}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                {form.cotejo_inicial
                  ? 'Cantidad inicial a AGREGAR por tipo de huevo'
                  : (canVerSistemaConteo ? 'Conteo por Tipo y Lote' : 'Ingrese la cantidad física contada por tipo/lote')}
              </Label>
              {!form.cotejo_inicial && (
                <p className="text-[10px] text-muted-foreground italic flex items-center gap-1">
                  <Layers className="w-3 h-3" /> El físico del tipo se suma automáticamente desde sus lotes
                </p>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo / Lote</TableHead>
                    {canVerSistemaConteo && (
                      <TableHead className="text-right">
                        {form.cotejo_inicial ? 'Stock Actual' : 'Sistema'}
                      </TableHead>
                    )}
                    <TableHead className="text-right">
                      {form.cotejo_inicial ? 'Cantidad a Sumar' : 'Físico (contado)'}
                    </TableHead>
                    {canVerSistemaConteo && (
                      <TableHead className="text-right">
                        {form.cotejo_inicial ? 'Nuevo Total' : 'Diferencia'}
                      </TableHead>
                    )}
                    {canVerSistemaConteo && !form.cotejo_inicial && (
                      <TableHead className="text-center">Estado</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {TIPOS_CONTEO.map((tipo) => {
                    const c = form.conteos[tipo] || { sistema: 0, fisico: '' };
                    const sis = Number(c.sistema || 0);
                    const fis = parseInt(c.fisico, 10) || 0;
                    const dif = c.fisico === '' ? null : fis - sis;
                    let difCls = 'text-muted-foreground';
                    if (dif !== null) {
                      difCls = dif < 0
                        ? 'text-destructive font-semibold'
                        : (dif > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground');
                    }
                    let estado = '—';
                    let estadoVar = 'outline';
                    if (dif !== null) {
                      if (dif < 0) { estado = 'Faltante'; estadoVar = 'destructive'; }
                      else if (dif > 0) { estado = 'Sobrante'; estadoVar = 'secondary'; }
                      else { estado = 'OK'; estadoVar = 'default'; }
                    }
                    const nuevoTotal = sis + fis;
                    const lotesDeEsteTipo = rowsByTipo[tipo] || [];
                    // Modo COTEJO INICIAL: no mostramos desglose por lote (se aplica solo al total).
                    const mostrarLotes = !form.cotejo_inicial && lotesDeEsteTipo.length > 0;
                    return (
                      <React.Fragment key={tipo}>
                        {/* Fila del tipo (total) */}
                        <TableRow className={mostrarLotes ? 'bg-muted/30' : ''}>
                          <TableCell className="font-semibold">
                            {TIPO_LABELS_CONTEO[tipo]}
                            {mostrarLotes && (
                              <span className="ml-2 text-[10px] text-muted-foreground font-normal">
                                (suma de {lotesDeEsteTipo.length} lote{lotesDeEsteTipo.length === 1 ? '' : 's'})
                              </span>
                            )}
                          </TableCell>
                          {canVerSistemaConteo && (
                            <TableCell className="text-right tabular-nums text-muted-foreground font-medium">
                              {sis.toLocaleString('en-US')}
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            {mostrarLotes ? (
                              <div
                                className="h-9 text-right tabular-nums w-32 ml-auto font-semibold flex items-center justify-end text-sm"
                                data-testid={`total-fisico-${tipo}`}
                              >
                                {c.fisico === '' ? '—' : fis.toLocaleString('en-US')}
                              </div>
                            ) : (
                              <Input
                                type="number"
                                min="0"
                                value={c.fisico}
                                onChange={(e) => setConteoValue(tipo, 'fisico', e.target.value)}
                                className="h-9 text-right tabular-nums w-32 ml-auto"
                                placeholder="0"
                                data-testid={`input-fisico-${tipo}`}
                              />
                            )}
                          </TableCell>
                          {canVerSistemaConteo && form.cotejo_inicial && (
                            <TableCell className="text-right tabular-nums text-green-600 font-semibold">
                              {c.fisico === '' ? '—' : nuevoTotal.toLocaleString('en-US')}
                            </TableCell>
                          )}
                          {canVerSistemaConteo && !form.cotejo_inicial && (
                            <>
                              <TableCell className={`text-right tabular-nums ${difCls}`}>
                                {dif === null
                                  ? '—'
                                  : (dif > 0
                                      ? `+${dif.toLocaleString('en-US')}`
                                      : dif.toLocaleString('en-US'))}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant={estadoVar} className="text-xs">{estado}</Badge>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                        {/* Filas por lote (solo si no es cotejo inicial y hay lotes) */}
                        {mostrarLotes && lotesDeEsteTipo.map((r) => {
                          const sisL = Number(r.sistema || 0);
                          const fisL = parseInt(r.fisico, 10) || 0;
                          const difL = r.fisico === '' ? null : fisL - sisL;
                          let difLCls = 'text-muted-foreground';
                          if (difL !== null) {
                            difLCls = difL < 0
                              ? 'text-destructive'
                              : (difL > 0 ? 'text-green-600' : 'text-muted-foreground');
                          }
                          let estadoL = '—';
                          let estadoLVar = 'outline';
                          if (difL !== null) {
                            if (difL < 0) { estadoL = 'Faltante'; estadoLVar = 'destructive'; }
                            else if (difL > 0) { estadoL = 'Sobrante'; estadoLVar = 'secondary'; }
                            else { estadoL = 'OK'; estadoLVar = 'default'; }
                          }
                          return (
                            <TableRow
                              key={`${tipo}-${r.lote_id}`}
                              className="bg-background hover:bg-accent/30 transition-colors"
                              data-testid={`row-lote-${tipo}-${r.lote_id}`}
                            >
                              <TableCell className="pl-8 text-sm">
                                <div className="flex items-center gap-1.5">
                                  <Layers className="w-3 h-3 text-muted-foreground" />
                                  <span className="font-mono">{formatLoteLabel(r.lote_codigo, r.lote_id)}</span>
                                </div>
                              </TableCell>
                              {canVerSistemaConteo && (
                                <TableCell className="text-right tabular-nums text-muted-foreground text-sm">
                                  {sisL.toLocaleString('en-US')}
                                </TableCell>
                              )}
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  min="0"
                                  value={r.fisico}
                                  onChange={(e) => setLoteFisico(tipo, r.lote_id, e.target.value)}
                                  className="h-8 text-right tabular-nums w-32 ml-auto text-sm"
                                  placeholder="0"
                                  data-testid={`input-fisico-lote-${tipo}-${r.lote_id}`}
                                />
                              </TableCell>
                              {canVerSistemaConteo && (
                                <TableCell className={`text-right tabular-nums text-sm ${difLCls}`}>
                                  {difL === null
                                    ? '—'
                                    : (difL > 0
                                        ? `+${difL.toLocaleString('en-US')}`
                                        : difL.toLocaleString('en-US'))}
                                </TableCell>
                              )}
                              {canVerSistemaConteo && (
                                <TableCell className="text-center">
                                  <Badge variant={estadoLVar} className="text-[10px] px-1.5 py-0">
                                    {estadoL}
                                  </Badge>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div>
            <Label htmlFor="conteo-obs">Observaciones</Label>
            <textarea
              id="conteo-obs"
              rows={3}
              value={form.observaciones}
              onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
              className="w-full mt-1.5 px-3 py-2 text-sm border rounded-md bg-background min-h-[72px]"
              placeholder="Anotaciones, motivo de diferencias, responsable del conteo..."
              data-testid="input-observaciones-conteo"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={savingConteo}>
            Cancelar
          </Button>
          <Button onClick={onSave} disabled={savingConteo} data-testid="btn-guardar-conteo">
            {savingConteo
              ? (editing ? 'Actualizando...' : 'Guardando...')
              : (editing
                  ? (form.cotejo_inicial ? 'Actualizar Cotejo Inicial' : 'Actualizar Conteo')
                  : (form.cotejo_inicial ? 'Sumar al Inventario' : 'Guardar Conteo'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
